import {
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  Popover,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  Code,
  Copy,
  Info,
  Play,
  XCircle,
} from '@phosphor-icons/react';
import { useState } from 'react';
import { FlowService } from '../../../service/FlowService';
import { useNotificationStore } from '../../../store/storeNotifications';

interface PublishProps {
  chatData: any;
  onSetFlow: any
}

export function Publish({ chatData, onSetFlow }: PublishProps) {
  const [isPublishing, setIsPublishing] = useState<boolean>(chatData.public);
  const setNotification = useNotificationStore(state => state.setNotification);
  const [isPopover, setIsPopover] = useState<HTMLButtonElement | null>(null);
  const [typeEmbed, setTypeEmbed] = useState(true);
  const [data, setData] = useState(chatData);

  function handleCopy(type: string) {
    switch (type) {
      case 'linkChat':
        navigator.clipboard.writeText(linkChat).then(() => {
          setNotification({
            type: 'success',
            message: 'URL copy',
          });
        });
        break;

      case 'iframe':
        navigator.clipboard.writeText(iframeCode).then(() => {
          setNotification({
            type: 'success',
            message: 'Iframe copy',
          });
        });
        break;

      case 'script':
        navigator.clipboard.writeText(scriptCode).then(() => {
          setNotification({
            type: 'success',
            message: 'Script copy',
          });
        });
        break;

      default:
        setNotification({
          type: 'error',
          message: 'Error copying content',
        });
    }
  }

  function handleSwitchChange(event: React.ChangeEvent<HTMLInputElement>) {
    setIsPublishing(event.target.checked);
  }

  async function handlePublish() {
    try {
      const response = await FlowService.updateFlow(chatData.id, {
        public: isPublishing,
      });

      onSetFlow(response.data)
      
      setNotification({
        type: 'success',
        message: 'Saved successfully',
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Error publishing',
      });
    }
  }

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsPopover(event.currentTarget);
  };
  const handleClosePopover = () => {
    setIsPopover(null);
  };

  function handleTypeEmbed() {
    setTypeEmbed(!typeEmbed);
  }

  const linkChat = `${window.location.origin}/chatbot/${chatData.id}`;
  const iframeCode = `<iframe src='${window.location.origin}/chatbot/${chatData.id}' height='800px' width='600px' style="border: none;"></iframe>`;
  const scriptCode = `<script src='${window.location.origin}/embed-script.js'></script>
  <flux-prompt-chat src='${window.location.origin}/chatbot/${chatData.id}' width="600px" height="800px"></flux-prompt-chat>`;

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        width="100%"
        height="600px"
      >
        <Box display="flex" flexDirection="column" gap="16px" height="100%">
          <Box
            display="flex"
            alignItems="center"
            gap="8px"
            width="100%"
            mb="8px"
          >
            <Play size={20} color="#101828" />
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: '500',
                color: '#101828',
              }}
            >
              Publish
            </Typography>

            {chatData.public && (
              <Chip
                icon={<CheckCircle size={24} />}
                label="Published"
                color="success"
                variant="outlined"
              />
            )}

            {!chatData.public && (
              <Chip
                icon={<XCircle size={24} />}
                label="Unpublished"
                color="error"
                variant="outlined"
              />
            )}
          </Box>

          <Box display="flex" gap="8px" alignItems="center">
            <Typography
              sx={{ color: '#475467', fontSize: '16px', fontWeight: '600' }}
            >
              Publishing OFF
            </Typography>
            <Switch checked={isPublishing} onChange={handleSwitchChange} />
            <Typography
              sx={{ color: '#475467', fontSize: '16px', fontWeight: '600' }}
            >
              Publishing ON
            </Typography>
          </Box>

          <Box display="flex" gap="8px" alignItems="center">
            <Info size={24} />

            <Typography
              sx={{ color: '#475467', fontSize: '16px', fontWeight: '400' }}
            >
              Once you publish your chatbot, anyone with the link will be able
              to view it.
            </Typography>
          </Box>

          <Divider />

          <TextField
            id="outlined-basic"
            label="Public URL"
            variant="outlined"
            value={linkChat}
            helperText="To use a custom domain or restrict access with a password, add this chatbot to an Interface."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Copy
                    onClick={() => handleCopy('linkChat')}
                    size={24}
                    style={{ cursor: 'pointer' }}
                  />
                </InputAdornment>
              ),
            }}
          />

          <Box display="flex" justifyContent="end">
            <Button
              variant="text"
              sx={{ gap: '4px' }}
              onClick={handleOpenPopover}
            >
              <Code size={20} /> Embed
            </Button>
          </Box>
        </Box>

        <Box display="flex" justifyContent="end" width="100%">
          <Button
            variant="contained"
            onClick={handlePublish}
            sx={{ width: '160px' }}
          >
            Save changes
          </Button>
        </Box>
      </Box>

      <Popover
        open={Boolean(isPopover)}
        anchorEl={isPopover}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          gap="24px"
          padding="24px"
          maxWidth="340px"
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" gap="4px">
              <Button
                variant={typeEmbed ? 'contained' : 'outlined'}
                onClick={handleTypeEmbed}
              >
                Script
              </Button>
              <Button
                variant={!typeEmbed ? 'contained' : 'outlined'}
                onClick={handleTypeEmbed}
              >
                iframe
              </Button>
            </Box>

            <Button
              variant="text"
              sx={{ gap: '8px' }}
              onClick={() => handleCopy(typeEmbed ? 'script' : 'iframe')}
            >
              Copy code <Copy size={20} />
            </Button>
          </Box>

          {typeEmbed && (
            <Typography
              sx={{
                color: '#475467',
                fontSize: '16px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                flex: 1,
              }}
            >
              {scriptCode}
            </Typography>
          )}

          {!typeEmbed && (
            <Typography
              sx={{
                color: '#475467',
                fontSize: '16px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                flex: 1,
              }}
            >
              {iframeCode}
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
}
